import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, Cloud } from "lucide-react";
import { useApp } from "@/context/AppProviders";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";

const durations = ["monthly", "annual"];

const Shop = () => {
  const { t } = useApp();
  const p = t.pricing;
  const s = t.shop;
  const navigate = useNavigate();
  const [duration, setDuration] = useState("annual");
  const [addon, setAddon] = useState({ professional: false, expert: false });

  const goCheckout = (tierId) => {
    const portal = addon[tierId] ? 1 : 0;
    navigate(`/checkout?plan=${tierId}&billing=${duration}&portal=${portal}`);
  };

  return (
    <div className="min-h-screen bg-background text-foreground font-body antialiased">
      <Header />
      <main className="pt-28 pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {s.nav}
          </p>
          <h1 className="font-heading mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {s.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
            {s.subtitle}
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex rounded-sm border border-border bg-card p-1">
            {durations.map((d) => (
              <button
                key={d}
                onClick={() => setDuration(d)}
                data-testid={`shop-billing-${d}`}
                className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                  duration === d
                    ? "bg-blue-600 text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {p.billing[d]}
              </button>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground/70">{s.billingNote}</p>

          <div className="mt-8 grid gap-6 lg:grid-cols-3 items-start">
            {p.tiers.map((tier, i) => {
              const popular = tier.id === "professional";
              const canAddon = tier.id === "professional" || tier.id === "expert";
              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  data-testid={`shop-tier-${tier.id}`}
                  className={`relative flex flex-col rounded-sm border bg-card p-8 ${
                    popular
                      ? "border-blue-600 shadow-[0_0_0_1px_rgb(37,99,235),0_0_40px_-12px_rgb(37,99,235)]"
                      : "border-border"
                  }`}
                >
                  {popular && (
                    <span className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-sm bg-blue-600 px-2.5 py-1 text-xs font-medium text-white">
                      <Star className="h-3 w-3 fill-current" />
                      {p.popular}
                    </span>
                  )}
                  <h3 className="font-heading text-xl font-bold tracking-tight">
                    {tier.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground">{tier.desc}</p>

                  <div className="mt-6 border-y border-border py-5">
                    <div className="flex items-baseline gap-1.5">
                      <span
                        data-testid={`shop-price-${tier.id}`}
                        className="font-heading text-4xl font-bold tracking-tight"
                      >
                        {tier.prices[duration]}
                      </span>
                      <span className="font-mono text-sm text-muted-foreground">
                        {p.period[duration]}
                      </span>
                    </div>
                    <p className="mt-1.5 text-xs text-muted-foreground/70">{s.vat}</p>
                  </div>

                  <ul className="mt-6 flex-1 space-y-3">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm">
                        <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
                        <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Portal add-on */}
                  {canAddon && (
                    <label
                      data-testid={`shop-addon-${tier.id}`}
                      className="mt-5 flex cursor-pointer items-start gap-3 rounded-sm border border-blue-600/30 bg-blue-600/[0.06] p-3"
                    >
                      <input
                        type="checkbox"
                        checked={addon[tier.id]}
                        onChange={(e) =>
                          setAddon((a) => ({ ...a, [tier.id]: e.target.checked }))
                        }
                        className="mt-0.5 h-4 w-4 accent-blue-600"
                        data-testid={`shop-addon-checkbox-${tier.id}`}
                      />
                      <span className="text-xs leading-relaxed">
                        <span className="flex items-center gap-1.5 font-medium text-foreground">
                          <Cloud className="h-3.5 w-3.5 text-blue-500" />
                          {s.addonTitle} ({s.addonPriceLabel})
                        </span>
                        <span className="mt-1 block text-muted-foreground">
                          {s.addonDesc}
                        </span>
                      </span>
                    </label>
                  )}
                  {tier.id === "standard" && (
                    <p className="mt-5 text-xs italic leading-relaxed text-muted-foreground/60">
                      {s.addonStandard}
                    </p>
                  )}

                  <button
                    onClick={() => goCheckout(tier.id)}
                    data-testid={`shop-choose-${tier.id}`}
                    className={`mt-8 inline-flex h-11 items-center justify-center rounded-sm px-5 text-sm font-medium transition-colors duration-200 ${
                      popular
                        ? "bg-blue-600 text-white hover:bg-blue-500"
                        : "border border-border bg-secondary/50 hover:bg-secondary"
                    }`}
                  >
                    {s.choose} {tier.name}
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Shop;
